import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../store';
import TowStarter, { DataSourceOptionType } from '../../tow/TowStarter';
import { ToxnotComponentProperties } from '../core/BaseApplicationComponents/ToxnotComponent';
import { ToxnotSignInRequiredComponent } from '../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';

import './styles.css';

enum ImportClass {
    Products,
    Materials,
    Suppliers
}

enum InventoryModeType {
    One,
    Multiple
}

enum InventoryDataType {
    Bom,
    Sds,
    SummaryData,
    ExternalDbIdUpdate,
    HpdBuilderData,
    ScipData,
    IpcXml
}

interface StartImportState {
    selectedImportClass: ImportClass | null;
    inventoryMode: InventoryModeType | null;
    inventoryData: InventoryDataType | null;
}

interface SelectionMetadata {
    localizedName: string | JSX.Element;
    localizedDescription: string | JSX.Element;
}

enum TargetTowImportWorklowModeType {
    SingleProductSpreadsheet = "SingleProductSpreadsheet",
    SingleMaterialSpreadsheet = "SingleMaterialSpreadsheet",
    SingleProductSds = "SingleProductSds",
    SingleMaterialSds = "SingleMaterialSds",
    ProductLibrary = "ProductLibrary",
    MaterialLibrary = "MaterialLibrary",
    SdsLibrary = "SdsLibrary",
    HpdBuilder = "HpdBuilder",
    SummaryDataLibrary = "SummaryDataLibrary",
    SummaryDataSingleInventory = "SummaryDataSingleInventory",
    ExternalDbIdUpdate = "ExternalDbIdUpdate",
    ScipData = "ScipData",
    SupplierData = "SupplierData",
    TestReport = "TestReport",
    Ipc1752aXml = "Ipc1752aXml"
}

// ===============================================================================

//
// Sub start component for importing data. Walks the user through a wizard that
// helps them select their import type, and then kicks off a TOW for that
// type of import.
//
class StartImport extends ToxnotSignInRequiredComponent<ToxnotComponentProperties, StartImportState> {
    private _importClassToMetadata: { [importClass: number]: SelectionMetadata };
    private _inventoryModeToMetadata: { [inventoryMode: number]: SelectionMetadata };
    private _inventoryDataToMetadata: { [inventoryData: number]: SelectionMetadata };
    private _importClasses: ImportClass[];
    private _inventoryModes: InventoryModeType[];
    private _inventoryDataTypesByInventoryMode: { [inventoryMode: number]: InventoryDataType[] };
    private _shouldScrollToBottom: boolean;
    private _towStarter: TowStarter;

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // ===============================================================================

    /**
     * Instantiates the new object w/ the given properties
     * 
     * @param props
     */
    public constructor(props: ToxnotComponentProperties) {
        super(props);

        this._shouldScrollToBottom = false;

        this.state = {
            selectedImportClass: null,
            inventoryMode: null,
            inventoryData: null
        };

        this._towStarter = new TowStarter();

        this._importClassToMetadata = {
            [ImportClass.Products]: {
                localizedName: this.localizeString("Products"),
                localizedDescription: this.localizeString("Products are final finished goods, in most cases what your company sells.")
            },
            [ImportClass.Materials]: {
                localizedName: this.localizeString("Materials"),
                localizedDescription: this.localizeString("Materials are components or materials that make up your finished products.")
            },
            [ImportClass.Suppliers]: {
                localizedName: this.localizeString("Suppliers"),
                localizedDescription: this.localizeString("Supplier information lets you set names and contacts for suppliers so you can send Surveys.")
            }
        };

        this._importClasses = [ImportClass.Products, ImportClass.Materials, ImportClass.Suppliers];

        this._inventoryModeToMetadata = {
            [InventoryModeType.One]: {
                localizedName: this.localizeString("One Item"),
                localizedDescription: this.localizeString("Import data for a single Product or Material")
            },
            [InventoryModeType.Multiple]: {
                localizedName: this.localizeString("Many Item"),
                localizedDescription: this.localizeString("Import data for a mulitple Products or Materials at once")
            }
        };

        this._inventoryModes = [InventoryModeType.One, InventoryModeType.Multiple];

        this._inventoryDataToMetadata = {
            [InventoryDataType.Bom]: {
                localizedName: this.localizeString("BOM Data"),
                localizedDescription: this.localizeString("A Bill of Materials spreadsheet with data on your sub-materials and ingredients. e.g. Part Name, % by weight, CASRN, Levels")
            },
            [InventoryDataType.Sds]: {
                localizedName: this.localizeString("SDS Files"),
                localizedDescription: this.localizeString("You can import an SDS PDF file and 3E Exchange will extract ingredients and other data out of it.")
            },
            [InventoryDataType.SummaryData]: {
                localizedName: this.localizeString("Compliance/Summary Data"),
                localizedDescription: this.localizeString("Non-BOM Data about your Product like compliance status, general description, volume sold, etc.")
            },
            [InventoryDataType.ExternalDbIdUpdate]: {
                localizedName: this.localizeString("Bulk Update External DB ID"),
                localizedDescription: this.localizeString("You can bulk update existing External DB ID for your products/materials by specifying the existing Part Name, Description, Supplier Name, Plant, and External DB ID, and the new External DB ID you want to update to.")
            },
            [InventoryDataType.HpdBuilderData]: {
                localizedName: this.localizeString("Data from HPD Builder"),
                localizedDescription: this.localizeString("You can import products from the HPD Builder service. You will need to sign in to HPD Builder.")
            },
            [InventoryDataType.ScipData]: {
                localizedName: this.localizeString("EU SCIP Data"),
                localizedDescription: this.localizeString("You can bulk import EU SCIP-related data, e.g. article composition & categories.")
            },
            [InventoryDataType.IpcXml]: {
                localizedName: this.localizeString("IPC XML Files"),
                localizedDescription: this.localizeString("You can upload an IPC1752A XML file and 3E Exchange will extract BOM and compliance data from it. You can bulk import IPC1752A XML files.")
            }
        };

        this._inventoryDataTypesByInventoryMode = {
            [InventoryModeType.One]: [
                InventoryDataType.Bom,
                InventoryDataType.Sds,
                InventoryDataType.SummaryData,
                InventoryDataType.HpdBuilderData,
                InventoryDataType.ScipData,
                InventoryDataType.IpcXml
            ],
            [InventoryModeType.Multiple]: [
                InventoryDataType.Bom,
                InventoryDataType.Sds,
                InventoryDataType.SummaryData,
                InventoryDataType.HpdBuilderData,
                InventoryDataType.ScipData,
                InventoryDataType.IpcXml,
                InventoryDataType.ExternalDbIdUpdate
            ]
        };
    }

    // ===============================================================================

    /**
     * Side effects that happen whenever the state is updated - scroll to the bottom of the screen as needed.
     */
    public componentDidUpdate() {
        if (this._shouldScrollToBottom && $("#uat-banner")?.length == 0) {
            $("#scroll-end-target")[0].scrollIntoView({ behavior: "smooth", block: "end" });

            this._shouldScrollToBottom = false;
        }
    }

    // ===============================================================================

    /**
     * Renders the main view
     */
    public renderConcreteComponent() {
        const availableInventoryDataTypes = this.state.inventoryMode !== null
            ? this._inventoryDataTypesByInventoryMode[this.state.inventoryMode]
            : [];

        return (
            <div id="start-import-content" className="start-content">
                <div className="full-content">
                    <div className="start-subcontent-heading">
                        {this.localizeString("Import Products, Materials, or Suppliers")}
                    </div>
                    <p className="start-subcontent-content-container">
                        {this.localizeString("What type of data do you want to import?")}<br />
                    </p>
                    <div className="start-subcontent-content-container">
                        <div className="selectors-container">
                            {this._importClasses.map((importClass, importClassIndex) => (
                                <div key={importClassIndex} className={("selector-div " + ((this.state.selectedImportClass === importClass) ? "selected" : ""))} onClick={e => this.importClassSelector_click(importClass)}>
                                    <div className="title">{this._importClassToMetadata[importClass].localizedName}</div>
                                    <div className="description">{this._importClassToMetadata[importClass].localizedDescription}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {((this.state.selectedImportClass === ImportClass.Products) || (this.state.selectedImportClass === ImportClass.Materials)) && 
                        <React.Fragment>
                            <p className="start-subcontent-content-container">
                                {this.localizeString("Are you uploading data for one item or multiple items?")}
                            </p>
                            <div className="start-subcontent-content-container">
                                <div className="selectors-container">
                                    {this._inventoryModes.map((inventoryMode, inventoryModeIndex) => (
                                        <div key={inventoryModeIndex} className={("selector-div " + ((this.state.inventoryMode === inventoryMode) ? "selected" : ""))} onClick={e => this.inventoryModeSelector_click(inventoryMode)}>
                                            <div className="title">{this._inventoryModeToMetadata[inventoryMode].localizedName}</div>
                                            <div className="description">{this._inventoryModeToMetadata[inventoryMode].localizedDescription}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {(this.state.inventoryMode !== null) &&
                                <React.Fragment>
                                    <p className="start-subcontent-content-container">
                                        {this.localizeString("What type of data do you want to import?")}
                                    </p>
                                    <div className="start-subcontent-content-container">
                                        <div className="selectors-container">
                                            {availableInventoryDataTypes.map((inventoryData, inventoryDataIndex) => (
                                                <div key={inventoryDataIndex} className={("selector-div " + ((this.state.inventoryData === inventoryData) ? "selected" : ""))} onClick={e => this.inventoryDataSelector_click(inventoryData)}>
                                                    <div className="title">{this._inventoryDataToMetadata[inventoryData].localizedName}</div>
                                                    <div className="description">{this._inventoryDataToMetadata[inventoryData].localizedDescription}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    {(this.state.inventoryData === InventoryDataType.Bom) &&
                                        <React.Fragment>
                                            {(this.state.inventoryMode === InventoryModeType.One) &&
                                                <React.Fragment>
                                                    <p className="start-subcontent-content-container">
                                                        {this.localizeString("You can update existing data or create new suppliers. Your imported data will be available in your My Suppliers library.")}
                                                    </p>
                                                    <p className="start-subcontent-content-container">
                                                        {this.localizeString("First, download & fill out an Import Template.")}
                                                    </p>
                                                    <div className="start-subcontent-content-container">
                                                        <div className="dropdown">
                                                            <button className="btn btn-default dropdown-toggle" type="button" id="bom-templates" data-toggle="dropdown" aria-haspopup="true" aria-expanded="true">
                                                                {this.localizeString("Download Import Template")} <span className="caret"></span>
                                                            </button>
                                                            <ul className="dropdown-menu" aria-labelledby="bom-templates">
                                                                <li><a href="/files/BomTemplates/Simple%20Product%20BOM%20template.xlsx">{this.localizeString("Simple BOM Template")}</a></li>
                                                                <li><a href="/files/BomTemplates/All%20Fields%20Template.xlsx">{this.localizeString("Advanced BOM Template (All Fields)")}</a></li>
                                                                <li><a href="/files/BomTemplates/Declare%20Label%20Template.xlsx">{this.localizeString("Declare Label Template")}</a></li>
                                                                <li><a href="/files/BomTemplates/HPD%20Template.xlsx">{this.localizeString("HPD Template")}</a></li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </React.Fragment>
                                            }
                                            {(this.state.inventoryMode === InventoryModeType.Multiple) &&
                                                <React.Fragment>
                                                    <p className="start-subcontent-content-container">
                                                        {this.localizeString("You can easily import your entire Product or Material Library into 3E Exchange. Set up a spreadsheet that contains BOM data for all of your Products/Materials.")}
                                                    </p>
                                                    <p className="start-subcontent-content-container">
                                                        {this.localizeString("First, download & fill out an Import Template.")}
                                                    </p>
                                                    <p className="start-subcontent-content-container">
                                                        <a href="/files/BomTemplates/Product%20Library%20Template.xlsx" className="btn btn-default">{this.localizeString("Download Import Template")}</a>
                                                    </p>
                                                </React.Fragment>
                                            }
                                        </React.Fragment>
                                    }
                                    {(this.state.inventoryData === InventoryDataType.Sds) &&
                                        <React.Fragment>
                                            <p className="start-subcontent-content-container">
                                                {this.localizeString("You can import SDS PDF files. 3E Exchange will scan the PDFs and automatically import ingredient data along with other SDS data.")}
                                            </p>
                                            <p className="start-subcontent-content-container">
                                                {this.localizeString("You'll be able to QA the result and correct any issues with the data.")}
                                            </p>
                                        </React.Fragment>
                                    }
                                    {(this.state.inventoryData === InventoryDataType.SummaryData) &&
                                        <React.Fragment>
                                            <p className="start-subcontent-content-container">
                                                {this.localizeString("Summary Data is non-BOM, non-ingredient data. e.g. Compliance determinations, general information about a Product/Material.")}
                                            </p>
                                            <p className="start-subcontent-content-container">
                                                {this.localizeString("Download & fill out this import Template. The import template shows you all the fields you can import.")}
                                            </p>
                                            <p className="start-subcontent-content-container">
                                                <a href="/files/Summary Data Library Import Template (One Row Per Product).xlsx" className="btn btn-default">{this.localizeString("Download Import Template")}</a>
                                            </p>
                                        </React.Fragment>
                                    }
                                    {(this.state.inventoryData === InventoryDataType.ExternalDbIdUpdate) &&
                                        <React.Fragment>
                                            <p className="start-subcontent-content-container">
                                                {this.localizeString("Use this import to update External DB IDs for existing Products or Materials. Matching uses Part Name, Description, Supplier Name, Plant, and Current External DB ID when provided.")}
                                            </p>
                                            <p className="start-subcontent-content-container">
                                                {this.localizeString("Download & fill out the template.")}
                                            </p>
                                            <p className="start-subcontent-content-container">
                                                <a href="/files/External DB ID Update Import Template.csv" className="btn btn-default">{this.localizeString("Download Import Template")}</a>
                                            </p>
                                        </React.Fragment>
                                    }
                                    {(this.state.inventoryData === InventoryDataType.HpdBuilderData) &&
                                        <React.Fragment>
                                            <p className="start-subcontent-content-container">
                                                {this.localizeString("You can import product data from the HPD Builder service.")}
                                            </p>
                                            <p className="start-subcontent-content-container">
                                                {this.localizeString("You'll need to sign in to your HPD Builder account to connect and then you can import any products you have.")}
                                            </p>
                                        </React.Fragment>
                                    }
                                    {(this.state.inventoryData === InventoryDataType.ScipData) &&
                                        <React.Fragment>
                                            <p className="start-subcontent-content-container">
                                                {this.localizeString("You can import SCIP-related data for one or many products/materials. Please use the template below for your import:")}
                                            </p>
                                            <p className="start-subcontent-content-container">
                                                <a href="/files/bulk-scip-import-template.xlsx" className="btn btn-default">{this.localizeString("Download Import Template")}</a>
                                            </p>
                                        </React.Fragment>
                                    }
                                    {(this.state.inventoryData === InventoryDataType.IpcXml) &&
                                        <React.Fragment>
                                            <p className="start-subcontent-content-container">
                                                {this.localizeString("You can import IPC1752A XML files. 3E Exchange will extract and import BOM and compliance data from it for multiple products.")}
                                            </p>
                                        </React.Fragment>
                                    }
                                </React.Fragment>
                            }
                        </React.Fragment>
                    }
                    {(this.state.selectedImportClass === ImportClass.Suppliers) &&
                        <React.Fragment>
                            <p className="start-subcontent-content-container">
                                {this.localizeString("You can update existing data or create new suppliers. Your imported data will be available in your My Suppliers library.")}
                            </p>
                            <p className="start-subcontent-content-container">
                                {this.localizeString("You can send Surveys to the suppliers in your library.")}
                            </p>
                            <p className="start-subcontent-content-container">
                                {this.localizeString("First, download & fill out this import Template.")}
                            </p>
                            <p className="start-subcontent-content-container">
                                <a href="/files/Supplier Import Template.xlsx" className="btn btn-default">{this.localizeString("Download Import Template")}</a>
                            </p>
                        </React.Fragment>
                    }
                    {((this.state.selectedImportClass === ImportClass.Suppliers) || ((this.state.inventoryMode !== null) && (this.state.inventoryData !== null))) &&
                        <React.Fragment>
                            <p className="start-subcontent-content-container">
                                {this.localizeString("Start the Import Workflow when you have your import files ready.")}
                            </p>
                            <p className="start-subcontent-content-container">
                                <button type="button" className="btn btn-toxnot-complement" onClick={async (e) => await this.startWorkflowButton_click()}>{this.localizeString("Start Import Workflow")} <span className="glyphicon glyphicon-chevron-right"></span></button>
                            </p>
                        </React.Fragment>
                    }
                    <div id="scroll-end-target"></div>
                </div>
            </div>
            );
    }

    // ===============================================================================

    //
    // UI Events
    //

    private importClassSelector_click = (importClass: ImportClass) => {
        this._shouldScrollToBottom = true;

        if (importClass === ImportClass.Products) {
            this._inventoryModeToMetadata[InventoryModeType.One].localizedName = this.localizeString("One Product");
            this._inventoryModeToMetadata[InventoryModeType.Multiple].localizedName = this.localizeString("Many Products");
        }
        else if (importClass === ImportClass.Materials) {
            this._inventoryModeToMetadata[InventoryModeType.One].localizedName = this.localizeString("One Material");
            this._inventoryModeToMetadata[InventoryModeType.Multiple].localizedName = this.localizeString("Many Materials");
        }

        this.setState({
            selectedImportClass: importClass,
            inventoryMode: null,
            inventoryData: null
        });
    }

    // ===============================================================================

    private inventoryModeSelector_click = (inventoryMode: InventoryModeType) => {
        this._shouldScrollToBottom = true;

        this.setState({
            inventoryMode: inventoryMode,
            inventoryData: null
        });
    }

    // ===============================================================================

    private inventoryDataSelector_click = (inventoryData: InventoryDataType) => {
        this._shouldScrollToBottom = true;

        this.setState({
            inventoryData: inventoryData
        });
    }

    // ===============================================================================

    private startWorkflowButton_click = async () => {
        let importMode: TargetTowImportWorklowModeType | null = null;
        let towOption: DataSourceOptionType | null = null;

        if (this.state.selectedImportClass === ImportClass.Suppliers) {
            importMode = TargetTowImportWorklowModeType.SupplierData;
            towOption = DataSourceOptionType.ImportSupplierData;
        }
        else {
            // products/materials
            if (this.state.inventoryMode === InventoryModeType.One) {
                switch (this.state.inventoryData) {
                    case InventoryDataType.Bom:
                        importMode = this.state.selectedImportClass === ImportClass.Products
                            ? TargetTowImportWorklowModeType.SingleProductSpreadsheet
                            : TargetTowImportWorklowModeType.SingleMaterialSpreadsheet;
                        break;
                    case InventoryDataType.Sds:
                        importMode = this.state.selectedImportClass === ImportClass.Products
                            ? TargetTowImportWorklowModeType.SingleProductSds
                            : TargetTowImportWorklowModeType.SingleMaterialSds;
                        break;
                    case InventoryDataType.SummaryData:
                        importMode = TargetTowImportWorklowModeType.SummaryDataLibrary;
                        towOption = DataSourceOptionType.ImportSummaryData;
                        break;
                    case InventoryDataType.ExternalDbIdUpdate:
                        importMode = TargetTowImportWorklowModeType.ExternalDbIdUpdate;
                        towOption = DataSourceOptionType.ImportSummaryData;
                        break;
                    case InventoryDataType.HpdBuilderData:
                        importMode = TargetTowImportWorklowModeType.HpdBuilder;
                        towOption = DataSourceOptionType.ImportHpdBuilder;
                        break;
                    case InventoryDataType.ScipData:
                        importMode = TargetTowImportWorklowModeType.ScipData;
                        towOption = DataSourceOptionType.ImportScipData;
                        break;
                    case InventoryDataType.IpcXml:
                        importMode = TargetTowImportWorklowModeType.Ipc1752aXml;
                        towOption = DataSourceOptionType.Import;
                        break;
                }
            }
            else if (this.state.inventoryMode === InventoryModeType.Multiple) {
                switch (this.state.inventoryData) {
                    case InventoryDataType.Bom:
                        importMode = this.state.selectedImportClass === ImportClass.Products
                            ? TargetTowImportWorklowModeType.ProductLibrary
                            : TargetTowImportWorklowModeType.MaterialLibrary;
                        break;
                    case InventoryDataType.Sds:
                        importMode = TargetTowImportWorklowModeType.SdsLibrary;
                        break;
                    case InventoryDataType.SummaryData:
                        importMode = TargetTowImportWorklowModeType.SummaryDataLibrary;
                        towOption = DataSourceOptionType.ImportSummaryData;
                        break;
                    case InventoryDataType.ExternalDbIdUpdate:
                        importMode = TargetTowImportWorklowModeType.ExternalDbIdUpdate;
                        towOption = DataSourceOptionType.ImportSummaryData;
                        break;
                    case InventoryDataType.HpdBuilderData:
                        importMode = TargetTowImportWorklowModeType.HpdBuilder;
                        towOption = DataSourceOptionType.ImportHpdBuilder;
                        break;
                    case InventoryDataType.ScipData:
                        importMode = TargetTowImportWorklowModeType.ScipData;
                        towOption = DataSourceOptionType.ImportScipData;
                        break;
                    case InventoryDataType.IpcXml:
                        importMode = TargetTowImportWorklowModeType.Ipc1752aXml;
                        towOption = DataSourceOptionType.Import;
                        break;
                }
            }
        }

        if (importMode === null) {
            throw new Error("Non-implemented config");
        }

        this.props.appServices.trackEvent("get-started-start-import-workflow-clicked");
        this.props.appServices.trackEvent(`get-started-start-import-workflow-${importMode}-clicked`);

        const success = await this._towStarter.startTow(
            towOption ?? DataSourceOptionType.Import,
            "GetStartedImportData",
            (inception, activeStep) => ({
                inception: inception,
                activeStep: activeStep,
                importMode: importMode,
                singleResultInventoryId: null,
                multiResultInventoryIds: null,
                importId: null,
                sdsOverwrite: null
            }));

        if (!success) {
            this.props.appServices.displayApiErrorMessage();
        }
    }
}

// ===============================================================================

export default connect(
    (state: ApplicationState) => ({ appServices: state.appServices })
)(StartImport as any);
